import { ErrorField } from '../error-field';
import { useField } from '@/components/form';
import { InputLabel } from './input-label';
import clsx from 'clsx';
import { ReactNode } from 'react';
import inputStyles from './base-input-wrapper.module.css';
import ReactSelect from 'react-select';
import styles from './select.module.css';

export interface BaseOption {
  value: string;
  label: string;
}

export interface SelectProps<T extends BaseOption = BaseOption> extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'onChange'
> {
  options: T[];
  placeholder?: string;
  name: string;
  label?: ReactNode;
  subLabel?: ReactNode;
  errorMessage?: string;
  selectorClassName?: string;
  onChange?: (val: T) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  formatOptionLabel?: (value: T) => ReactNode;
  removeFilterOption?: boolean;
  noOptionsMessage?: (obj: { inputValue: string }) => ReactNode;
  menuIsOpen?: boolean;
}

export function Select<T extends BaseOption>({
  options,
  placeholder,
  name,
  label,
  subLabel,
  errorMessage,
  selectorClassName,
  onChange,
  inputValue,
  onInputChange,
  formatOptionLabel,
  id,
  removeFilterOption,
  noOptionsMessage,
  menuIsOpen,
  ...restProps
}: SelectProps<T>) {
  const { fieldProps, error, getValue } = useField<HTMLSelectElement>(name, restProps);
  const { className, ...restFieldProps } = fieldProps;
  const currentValue = getValue();

  return (
    <div className={clsx(styles.selectInput, className)}>
      {label && (
        <InputLabel
          label={label}
          subLabel={subLabel}
          required={fieldProps.required}
          htmlFor={id ?? name}
        />
      )}
      <ReactSelect
        options={options}
        placeholder={placeholder}
        inputId={id ?? name}
        classNames={{
          control: () =>
            clsx(
              'body-2',
              inputStyles.input,
              styles.control,
              (error || errorMessage) && styles.hasError,
              selectorClassName,
            ),
          indicatorSeparator: () => styles.indicatorSeparator,
          valueContainer: () => styles.base,
          input: () => styles.base,
          dropdownIndicator: () => clsx(styles.base, styles.dropdownIndicator),
          menu: () => styles.menu,
          menuList: () => styles.menuList,
          option: (state) =>
            clsx(
              styles.option,
              state.isSelected && styles.selectedOption,
              state.isFocused && styles.focusedOption,
            ),
        }}
        // There is no menuIsOpen state in the classNames object.
        styles={{
          dropdownIndicator: (base, { selectProps: { menuIsOpen } }) => ({
            ...base,
            transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }),
        }}
        inputValue={inputValue}
        onInputChange={onInputChange}
        formatOptionLabel={formatOptionLabel}
        filterOption={removeFilterOption ? null : undefined}
        noOptionsMessage={noOptionsMessage}
        menuIsOpen={menuIsOpen}
        isDisabled={restFieldProps.disabled}
        {...restFieldProps}
        defaultValue={options.find((option) => option.value === fieldProps.defaultValue)}
        value={options.find((option) => option.value === currentValue) ?? null}
        onChange={(selectedOption) => {
          fieldProps.onChange({
            target: {
              name,
              value: selectedOption?.value ?? '',
            },
          });

          if (onChange && selectedOption) {
            onChange(selectedOption);
          }
        }}
      />
      <ErrorField error={errorMessage ? { type: 'custom', message: errorMessage } : error} />
    </div>
  );
}
